"use client";

import Translations from "@translate/components/Chat/InteractiveTool/TrackerChatCard/TrackerChatCard";
import type { StartTrackingArgs } from "./types";
import { Icon } from "@ospweb/sdk-ui";
import { I18nUtil } from "@utils/i18n";
import { formatTrackerNextScheduleTime } from "@components/Chat/JsonRenderComponents/utils";
import { cn } from "utils";

const I18n = I18nUtil.createScoped(Translations);

export interface TrackerChatCardProps {
  args: StartTrackingArgs;
  isTracked?: boolean;
  disabled?: boolean;
  isActivating?: boolean;
  onStartTracking: () => void | Promise<void>;
  className?: string;
}

function formatTriggerLabel(
  triggerType: StartTrackingArgs["trigger_type"],
): string {
  switch (triggerType) {
    case "cron":
      return I18n.t("cron");
    case "once":
      return I18n.t("once");
    case "event_trigger":
    default:
      return I18n.t("event");
  }
}

function formatNextSchedule(args: StartTrackingArgs): string {
  const { next_triggered: nextTriggered } = args;
  if (nextTriggered == null || nextTriggered === "") {
    return I18n.t("--");
  }

  const formatted = formatTrackerNextScheduleTime(nextTriggered);
  return formatted || I18n.t("--");
}

/**
 * TrackerChatCard — Chat 内 Tracker 提案 / 已建卡（Figma `97553:361772` / `97553:361814`）
 *
 * Interactive Tool: `start_tracking`（Logic L6 接线）
 */
export function TrackerChatCard({
  args,
  isTracked = false,
  disabled = false,
  isActivating = false,
  onStartTracking,
  className,
}: TrackerChatCardProps) {
  const isCtaDisabled = disabled || isActivating || isTracked;

  return (
    <article
      className={cn(
        "flex w-full flex-col gap-3 overflow-clip rounded-3 border border-border_inverse_transparent10 bg-background_solid_neutral_primary p-3 md:max-w-[534px]",
        className,
      )}
      data-testid={
        isTracked ? "tracker-chat-card-tracked" : "tracker-chat-card-proposal"
      }
      data-node-id={isTracked ? "97553:361814" : "97553:361772"}
    >
      <header className="flex flex-col gap-2" data-testid="tracker-chat-card">
        <h3 className="title_l text-content_primary">{args.title}</h3>
        {args.description ? (
          <p className="body_s text-content_tertiary">{args.description}</p>
        ) : null}
      </header>

      <div
        className="h-px w-full bg-border_inverse_transparent10"
        aria-hidden
        data-testid="tracker-chat-card-divider"
      />

      <dl className="flex flex-col gap-2 m-0">
        <div className="flex items-center justify-between gap-2">
          <dt className="body_s text-content_tertiary m-0">
            {I18n.t("Trigger action")}
          </dt>
          <dd className="body_medium_s text-content_primary m-0 capitalize">
            {formatTriggerLabel(args.trigger_type)}
          </dd>
        </div>
        <div className="flex items-center justify-between gap-2">
          <dt className="body_s text-content_tertiary m-0">
            {I18n.t("Next schedule")}
          </dt>
          <dd className="body_medium_s text-content_primary m-0">
            {formatNextSchedule(args)}
          </dd>
        </div>
      </dl>

      {isTracked ? (
        <div
          className="flex h-9 w-full cursor-default items-center justify-center gap-1 rounded-2 border border-border_inverse_transparent10 bg-background_white_overlay10 px-3 py-2"
          data-testid="tracker-chat-card-tracked-cta"
        >
          <Icon name="check" size={16} className="text-content_primary" />
          <span className="body_medium_m text-content_primary whitespace-nowrap">
            {I18n.t("✓ Tracked")}
          </span>
        </div>
      ) : (
        <button
          type="button"
          className={cn(
            "button md_button primary_button flex h-9 w-fit min-w-[80px] items-center justify-center gap-0.5 rounded-2 px-3 py-2",
            isCtaDisabled && "pointer-events-none opacity-50",
          )}
          data-testid="tracker-chat-card-start-cta"
          disabled={isCtaDisabled}
          onClick={() => {
            void onStartTracking();
          }}
        >
          <span className="body_medium_m text-content_inverse_primary whitespace-nowrap">
            {I18n.t("Start tracking")}
          </span>
        </button>
      )}
    </article>
  );
}
